<template>
  <div>
    <template v-if="$userIsAdmin">
      <div class="form-group">
        <label class="control-label">Program Name</label>

        <input
          type="text"
          class="form-control"
          v-model="stateProgramName"
          v-validate="'required'"
          name="name"
          data-vv-as="program name"
        >

        <help-block field="name"/>
      </div><!-- /.form-group -->
    </template>

    <h2 class="mt-4" v-if="$userIsAdmin">Placements</h2>
    <h2 class="mt-4" v-else-if="$userIsCoordinator">Placement Dates</h2>

    <programs-single-details-placement-card
      v-for="(placement, index) in program.placements"
      :key="index"
      :creating="creating"
      :index="index"
      :placement="placement"
    />
    <div class="text-left" v-if="$userIsAdmin">
        <button
            v-if="!programIsArchived"
            class="btn btn-outline-dark mr-2"
            @click="addPlacement"
          >+ Placement</button>
    </div>
      <div class="card mt-4">
        <div class="card-header">
          <h3>Overall Performance Assessment</h3>
        </div>

        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
            <div class="form-group">
              <label class="control-label">Open Date</label>

              <div class="input-group" :class="[validationClasses('open_date')]">
              <span class="input-group-prepend">
                <span class="input-group-text">
                  <font-awesome :icon="['far', 'calendar-alt']"/>
                </span>
              </span>

                <flatpickr
                  class="form-control date-single"
                  name="open_date"
                  :config="dateConfig"
                  v-validate=""
                  data-vv-as="open date"
                  v-model="stateProgramOpenDate"
                  :disabled="$userIsCoordinator"
                />
              </div><!-- /.input-group -->

              <help-block field="open_date"/>
            </div><!-- /.form-group -->
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label class="control-label">Due Date</label>

                <div class="input-group" :class="[validationClasses('due_date')]">
              <span class="input-group-prepend">
                <span class="input-group-text">
                  <font-awesome :icon="['far', 'calendar-alt']"/>
                </span>
              </span>
                  <flatpickr
                    class="form-control date-single"
                    v-model="stateProgramDueDate"
                    name="due_date"
                    :config="dateConfig"
                    v-validate=""
                    data-vv-as="due date"
                    :disabled="$userIsCoordinator"
                  />
                </div><!-- /.input-group -->

                <help-block field="due_date"/>

              </div><!-- /.form-group -->
            </div>
          </div>
        </div>
    </div>

    <div class="save-actions" v-if="$userIsAdmin">
      <div class="text-right">
        <router-link :to="{ name: 'programs' }" class="btn btn-link btn-discard mr-2">Discard &amp; Exit</router-link>

        <ladda-button
          v-if="!programIsDraft"
          type="button"
          class="btn btn-danger btn-publish-program mr-2"
          :validate="false"
          :click="handleArchive"
        >{{ programIsArchived ? 'UnArchive' : 'Archive' }}</ladda-button>

        <ladda-button
          v-if="programIsDraft && !programIsArchived"
          type="button"
          class="btn btn-outline-dark mr-2"
          :click="saveAndExit"
        >Save Draft &amp; Exit</ladda-button>

        <ladda-button
          type="button"
          class="btn btn-success btn-publish-program"
          :validate="false"
          :click="publish"
          v-if="!programIsArchived"
        >{{ programIsPublished ? 'Save' : 'Publish' }}</ladda-button>
      </div>
    </div><!-- /.save-actions -->
  </div>
</template>

<script>
import { programComputed, programMethods } from 'vx/helpers'
import { stateMapper, helpers } from 'mixins/programs'
import { merge, forIn, each } from 'lodash'
import Flatpickr from 'vue-flatpickr-component'
import ProgramsSingleDetailsPlacementCard from './ProgramsSingleDetailsPlacementCard'
import { initialPlacementState } from '../../../src/vuex/modules/placements'

export default {
  mixins: [stateMapper, helpers],

  props: {
    creating: {
      required: true,
      type: Boolean,
      default: false
    }
  },

  components: {
    ProgramsSingleDetailsPlacementCard,
    Flatpickr
  },

  data () {
    return {
      dateConfig: {
        altInput: true,
        altFormat: 'd/m/Y',
        dateFormat: 'Y-m-d'
      }
    }
  },

  computed: {
    ...programComputed
  },

  methods: {
    ...programMethods,

    addPlacement () {
      const placement = merge(initialPlacementState(), { number: this.program.placements[this.program.placements.length - 1].number + 1 })
      this.program.placements.push(placement)
    },

    async saveAndExit () {
      if (this.programIsPublished) {
        return
      }

      let payload = merge({}, this.program, { status: 'draft' })

      if (await this.save(payload)) {
        this.$noty.success('Program draft has been saved successfully.')
        this.$router.push({ name: 'programs' })
      }
    },

    async publish () {
      let payload = merge({}, this.program, { status: 'published' })

      let message = this.programIsPublished
        ? 'Program has been saved successfully.'
        : 'Program has been published successfully.'

      if (await this.save(payload)) {
        this.program.status = 'published'
        this.$noty.success(message)
      }
    },

    async save (payload) {
      this.$validator.reset()

      try {
        if (this.creating) {
          await this.storeProgram(payload)

          this.$router.push({ name: 'programs.details', params: { id: this.program.id } })
        } else {
          await this.updateProgram({
            program: this.program,
            payload
          })
        }

        return true
      } catch (error) {
        if (error.response && error.response.status === 422) {
          this.renderServerErrors(error.response)
        }
      }
    },

    async handleArchive () {
      this.$validator.reset()

      if (this.programIsArchived) {
        if (await this.unArchiveProgram(this.program)) {
          this.$noty.success('Program has been restored successfully.')
          this.$router.push({ name: 'programs' })
        }
      } else {
        if (await this.archiveProgram(this.program)) {
          this.$noty.success('Program has been archived successfully.')
          this.$router.push({ name: 'programs' })
        }
      }
    },

    renderServerErrors (response) {
      forIn(response.data.errors, (errors, name) => {
        let scope
        let field

        if (name.indexOf('.') !== -1) {
          scope = `placement-${name.split('.')[1]}`

          field = this.$validator.fields.find({ name: name.split('.').pop(), scope })
        } else {
          scope = null

          field = this.$validator.fields.find({ name })
        }

        field.setFlags({ invalid: true, valid: false, validated: true })

        each(errors, (msg) => {
          this.errors.add({ field: field.name, id: field.id, msg, scope })
        })
      })
    }
  }
}
</script>
